// @flow
import 'isomorphic-fetch';
import React, { Suspense } from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import Slot from 'shared/editor/Slot';
import SocialIcon from 'shared/components/SocialIcon';
import EventPostCard from 'shared/components/EventPostCard';
import translations from 'shared/strings';
import { getEventDateInfo, getEventDateInfoCN } from 'shared/lib/dateUtils';
import type { $Slot } from '../../../types';
import { getAPIDomain, getWebDomain } from 'shared/lib/clientSettings';
import Icon from '@ff/ui/Icon';
import withThemeProvider from 'shared/hoc/withThemeProvider';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

const Slider = React.lazy(() => import(/* webpackChunkName: "react-slick" */ 'react-slick'));

declare var IS_NODE: boolean

if (!IS_NODE) {
  import(/* webpackChunkName: "slick-carousel" */ 'slick-carousel/slick/slick.css');
  import(/* webpackChunkName: "slick-carousel" */ 'slick-carousel/slick/slick-theme.css');
}

type TemplateSettings = {
  region: string
}

type Props = {
  classes: Object,
  slots: $Slot[],
  templateSettings: TemplateSettings,
  widgetPresets: Array<Object>,
  path: string
}

type SocialShareIcon = {
  url: string,
  icon: string
}

type Event = {
  _id: string,
  template: {
    props: {
      featured: boolean,
      region: 'US' | 'CN',
      url: string,
      start_date: {
        date: string
      },
      end_date: {
        date: string
      },
      event_name: string,
      event_description: string,
      event_location: string,
      event_address: string,
      background_image_url: string,
      background_position: string,
      background_scale: string,
      social_share: Array<SocialShareIcon>
    }
  }
}

type State = {
  filter: string,
  posts: Array<Event>,
  activeSlide: number,
  expanded: boolean,
}

class EventList extends React.Component<Props, State> {
  props: Props = this.props;

  state: State = {
    filter: 'Upcoming Events',
    posts: [],
    activeSlide: 0,
    expanded: false
  }

  componentDidMount = () => {
    this.fetchData();
  }

  fetchData = async () => {
    const { templateSettings: { region }} = this.props;
    const appDomain = getAPIDomain(region) || ''

    const res = await fetch(`${appDomain}/api/events?region=${region}`);

    if (res.error) {
      throw new Error(`[Error fetching event list from /events]: ${res.error}`);
    }
    const posts = await res.json();
    const postPages = posts.pages;

    this.setState({
      posts: postPages
    });
  }

  getHeroCarousel = ({
    c, region, featuredPosts, activeSlide, expanded
  }) => {
    const iconStyle = {
      fontSize: '20px',
      color: 'white',
      marginLeft: '8px',
      marginRight: '8px',
      textDecoration: 'none',
      cursor: 'pointer'
    };

    const timezone = (region === 'CN') ? 'Asia/Shanghai' : 'America/Los_Angeles';
    const appDomain = getWebDomain(region);

    if (featuredPosts.length > 0) {

      /* react-slick sometimes sets activeSlide to -1 */
      const slideIndex = (activeSlide >= 0 && activeSlide < featuredPosts.length) ?
        activeSlide : 0;
      const event = featuredPosts[slideIndex].template.props;

      const dateInfo = region === 'CN' ?
        getEventDateInfoCN(event.start_date.date, event.end_date.date, timezone) :
        getEventDateInfo(event.start_date.date, event.end_date.date, timezone);

      const {
        startDate,
        startTime,
        endDate,
        endTime,
        isSameDay
      } = dateInfo;

      const eventTime = `${startTime} - ${endTime}`;
      const eventDate = isSameDay ?
        `${startDate}` :
        `${startDate} ${translations.TO[region]}\n${endDate}`;

      return (
        <div>
          <a
            className={c.postTitle}
            href={event.url}
          >
            <div>
              {event.event_name}
            </div>
          </a>
          <div className={c.date}>
            { eventDate.split('\n').map((date, i) => <p key={i}>{date}</p>) }
            <p className={c.time}>{eventTime}</p>
          </div>
          <div className={c.location}>
            {event.event_location}
          </div>
          <a
            className={c.readMore}
            href={event.url}
          >
            {translations.EVENT_DETAILS[region]}
            <span className="material-icons">arrow_forward</span>
          </a>
          <div className={c.socialShare}>
            <Icon
              name="ff-icon-share"
              className={c.socialIcon}
              onMouseEnter={() => this.setState({ expanded: true })}
              onMouseLeave={() => this.setState({ expanded: false })}
            />
            <div
              className={classNames(c.iconList, expanded && c.expanded)}
              onMouseEnter={() => this.setState({ expanded: true })}
              onMouseLeave={() => this.setState({ expanded: false })}
            >
              {event.social_share.map(link => (
                <SocialIcon
                  key={`featured${link.url}`}
                  iconStyle={iconStyle}
                  link={link}
                  generateQRCode={`${appDomain}${event.url}`}
                />
              ))}
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  getPostCards = (otherPosts, filter) => {
    const { templateSettings: { region }} = this.props;
    const currentTabPosts = otherPosts ? otherPosts.filter((e) => {
      const { props } = e.template;
      const today = new Date();

      if (filter === 'Upcoming Events') {
        return new Date(props.start_date.date) > today;
      }
      if (filter === 'Ongoing Events') {
        return today >= new Date(props.start_date.date) &&
          today <= new Date(props.end_date.date);
      }
      return today > new Date(props.end_date.date);
    }) : [];

    if (currentTabPosts.length === 0) {
      let tabName;

      if (filter === 'Upcoming Events') {
        tabName = translations.UPCOMING_EVENTS[region];
      } else {
        tabName = filter === 'Ongoing Events' ?
          translations.ONGOING_EVENTS[region] : translations.PAST_EVENTS[region];
      }

      const tab = region === 'US' ?
        ` ${tabName} events.`.toLowerCase() : '';

      return (
        <div style={{ fontSize: '21px' }}>{`${translations.NO_EVENTS[region]}${tab}`}</div>
      );
    }

    return currentTabPosts.sort((a, b) => (filter === 'Past Events' ?
      new Date(b.template.props.start_date.date) - new Date(a.template.props.start_date.date) :
      new Date(a.template.props.start_date.date) - new Date(b.template.props.start_date.date)))
      .map(post => (
        <EventPostCard
          event={post.template.props}
          key={post._id}
        />
      ));
  }

  render() {
    const {
      classes: c, slots, widgetPresets, path, templateSettings: { region }
    } = this.props;
    const {
      filter, posts, activeSlide, expanded
    } = this.state;
    const { getPostCards } = this;

    if (posts.length === 0) {
      return (<div />);
    }
    const featuredPosts = posts.filter(p => p.template.props.featured);
    const otherPosts = posts.filter(p => !p.template.props.featured);

    const sliderSettings = {
      arrows: false,
      dots: featuredPosts.length > 1,
      dotsClass: 'slick-dots',
      infinite: featuredPosts.length > 1,
      speed: 800,
      easing: 'ease-in-out',
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: 0,
      autoplay: true,
      autoplaySpeed: 8000,
      draggable: false,
      afterChange: (i) => {
        this.setState({ activeSlide: i });
      }
    };
    const heroCarousel = this.getHeroCarousel({
      c, region, featuredPosts, activeSlide, expanded
    });
    const slideIndex = (activeSlide >= 0 && activeSlide < featuredPosts.length) ?
      activeSlide : 0;

    return (
      <div className={c.container}>
        <div className={c.menu}>
          <Slot id="menu-bar" slots={slots} widgetPresets={widgetPresets} path={path} />
        </div>
        <div className={c.content}>
          <div className={c.heroSection}>
            <div className={c.heroTitle}>
              <div className={c.eventListTitle}>
                <div className={c.pageTitle}>Event List</div>
              </div>
              <div className={c.separator} />
              {heroCarousel}
            </div>
            { featuredPosts.length > 0 &&
            <Suspense fallback={<div />}>
              <Slider {...sliderSettings}>
                {featuredPosts.map((post, i) => (
                  <a
                    className={c.imageLink}
                    href={featuredPosts[slideIndex].template.props.url}
                    key={i}
                  >
                    <div
                      className={c.image}
                      style={{ backgroundImage: `url(${post.template.props.background_image_url})` }}
                    />
                  </a>
                ))}
              </Slider>
            </Suspense>
            }
          </div>
          <div className={c.switchContainer}>
            <ul className={c.switches} style={{ listStyleType: 'none', cursor: 'pointer' }}>
              <li
                onClick={() => this.setState({ filter: 'Upcoming Events' })}
                className={classNames(c.switch, filter === 'Upcoming Events' ? c.selected : '')}
                style={{ borderBottom: filter === 'Upcoming Events' ? '3px solid' : '' }}
              >
                <div>{translations.UPCOMING_EVENTS[region]}</div>
              </li>
              <li
                onClick={() => this.setState({ filter: 'Ongoing Events' })}
                className={classNames(c.switch, filter === 'Ongoing Events' ? c.selected : '')}
                style={{ borderBottom: filter === 'Ongoing Events' ? '3px solid' : '' }}
              >
                {translations.ONGOING_EVENTS[region]}
              </li>
              <li
                onClick={() => this.setState({ filter: 'Past Events' })}
                className={classNames(c.switch, filter === 'Past Events' ? c.selected : '')}
                style={{ borderBottom: filter === 'Past Events' ? '3px solid' : '' }}
              >
                {translations.PAST_EVENTS[region]}
              </li>
            </ul>
          </div>

          <div className={c.otherPosts}>
            { getPostCards(otherPosts, filter)}
          </div>
        </div>
        <footer id="footer" className={c.footer}>
          <Slot id="footer" slots={slots} widgetPresets={widgetPresets} path={path} />
        </footer>
      </div>
    );
  }
}

const styles = {
  switchContainer: {
    display: 'flex',
    justifyContent: 'center'
  },
  switches: {
    paddingInlineStart: 0,
    width: '60%',
    display: 'flex',
    justifyContent: 'space-around',
    '@media(max-width: 768px)': {
      width: '100%'
    }
  },
  switch: {
    fontSize: '21px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY_ROBOTO,
    paddingBottom: '5px',
  },
  selected: {
    color: 'black',
  },
  menu: {
    width: '100%',
    position: 'relative',
    zIndex: '10'
  },
  footer: {
    width: '100%'
  },
  heroSection: {
    width: '100%',
    height: '75%',
    position: 'relative',
    fontFamily: FONT_FAMILY_ROBOTO,
    '& .slick-list': {
      height: '100%'
    },
    '& .slick-dots': {
      bottom: '25px'
    },
    '& .slick-dots li': {
      width: '10px',
      height: '10px'
    },
    '& .slick-dots li button:before': {
      fontSize: '10px',
      transition: 'all .2s ease',
      color: 'white'
    },
    '& .slick-dots li.slick-active button:before': {
      fontSize: '12px',
      color: 'white'
    },
    '@media(max-width: 960px)': {
      width: '100%',
      height: '100vh'
    }
  },
  heroTitle: {
    width: '30%',
    height: '100%',
    backgroundColor: '#141618',
    position: 'absolute',
    left: '0',
    top: '0',
    zIndex: '3',
    color: 'white',
    padding: '84px 36px 54px',
    '@media(max-width: 1200px)': {
      width: '40%'
    },
    '@media(max-width: 960px)': {
      left: '0',
      right: '0',
      bottom: '0',
      top: 'auto',
      width: '100%',
      height: '40%',
      padding: '18px 36px'
    }
  },
  tagline: {
    fontSize: '15px',
    fontWeight: '600',
    letterSpacing: '.1em',
    color: 'white',
    textTransform: 'uppercase'
  },
  pageTitle: {
    fontSize: '46px',
    fontFamily: 'Sectra',
    letterSpacing: '1.15px',
    color: 'white',
    marginTop: '18px',
    textTranform: 'uppercase'
  },
  separator: {
    height: '2px',
    width: '90px',
    backgroundColor: 'black',
    marginTop: '18px',
    marginBottom: '27px',
    '@media(max-width: 960px)': {
      display: 'none'
    }
  },
  date: {
    marginTop: '18px',
    fontSize: '15px',
    letterSpacing: '.1em',
    lineHeight: '1.75em',
    textTransform: 'uppercase',
    '& > p': {
      margin: 0
    }
  },
  time: {
    fontWeight: 300
  },
  location: {
    marginTop: '15px',
    fontSize: '15px',
    letterSpacing: '.1em',
    lineHeight: '1.75em'
  },
  postTitle: {
    fontSize: '34px',
    fontWeight: '300',
    marginTop: '18px',
    lineHeight: '1.5em',
    textDecoration: 'none',
    color: 'white',
  },
  readMore: {
    textDecoration: 'none',
    color: 'black',
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '.1em',
    marginTop: '18px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    '& > .material-icons': {
      fontSize: '14px',
      marginLeft: '5px'
    }
  },
  image: {
    width: '100%',
    height: '100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 'cover',
    '@media(max-width: 960px)': {
      height: '60vh'
    }
  },
  imageLink: {
    top: 0,
    left: 0,
    height: '75vh',
    width: '100%',
    display: 'block !important'
  },
  otherPosts: {
    padding: '49px 36px',
    paddingRight: '0',
    display: 'flex',
    flexWrap: 'wrap',
    background: '#f7f7f7',
    '@media(min-width: 769px)': {
      paddingLeft: '4.5%',
    },
    '@media(min-width: 1037px)': {
      paddingLeft: '3.2%',
    },
    '@media(max-width: 768px)': {
      padding: '36px 18px'
    },
  },
  socialShare: {
    padding: '18px 0',
    display: 'flex',
    alignItems: 'center'
  },
  iconList: {
    marginLeft: '10px',
    width: '0',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    transition: 'all .4s ease'
  },
  expanded: {
    width: '100%'
  },
  socialIcon: {
    fontSize: '15px',
    linegHeight: '1.2em',
    cursor: 'pointer'
  },
  eventListTitle: {
    '@media(max-width: 960px)': {
      display: 'none'
    }
  }
};

export default withThemeProvider(injectSheet(styles)(EventList));
